import {Action, Card, DataStatus, useDialog} from 'platform/components';
import {Box} from 'platform/foundation';

import {useCallback} from 'react';

import {find, isNil, mergeAll} from 'ramda';

import {useGetVehicleCustomerQuery} from '@dms/api/customer';
import {
  DOCUMENT_ACTION_COLUMNS,
  useGetDocumentDatagridActions,
} from '@dms/features/document-context';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {DmsDataGrid, refreshDmsDatagrid, Section, UploadDocumentsDialog} from '@dms/shared';

import {buildArray, Nullish} from 'shared/web';

import type {QueryFilterObject} from 'features/eag-grid';

import {useRefreshDocumentCount} from '../../../../hooks/useRefreshDocumentCount';
import {useWorkshopUrl} from '../../../../hooks/useWorkshopUrl';

interface ServiceCaseDocumentsProps {
  vehicleId: string | Nullish;
  isReadOnly?: boolean;
}

export function ServiceCaseDocuments(props: ServiceCaseDocumentsProps) {
  const {serviceCaseId} = useWorkshopUrl();
  const refreshDocumentCount = useRefreshDocumentCount(serviceCaseId);

  const {selectedOwnership, isSelectedOwnershipLoading, isSelectedOwnershipError} =
    useGetVehicleCustomerQuery(
      {vehicleId: props.vehicleId!},
      {
        skip: isNil(props.vehicleId),
        selectFromResult: ({data, isLoading, isError, isFetching}) => ({
          selectedOwnership: find(
            (ownership) => ownership?.customerType === 'CORVEHICLECUSTOMERTYPE_OPERATOR',
            data ?? []
          ),
          isSelectedOwnershipLoading: isLoading,
          isSelectedOwnershipError: isError,
          isSelectedOwnershipFetching: isFetching,
        }),
      }
    );

  const [actionCallback, bulkSignatureAction] = useGetDocumentDatagridActions({
    refreshData: () => refreshDmsDatagrid('document-context-list-service-case'),
    customerId: selectedOwnership?.customerId,
    contextTarget: 'service-case',
    contextId: serviceCaseId,
    isDisabled: props.isReadOnly,
  });

  const [isUploadDialogOpen, openUploadDialog, closeUploadDialog] = useDialog();

  const handleDocumentCreated = () => {
    refreshDmsDatagrid('document-context-list-service-case');
    refreshDocumentCount();
  };

  const queryModifier = useCallback(
    (filter: QueryFilterObject) => mergeAll([filter, {serviceCaseId}]),
    [serviceCaseId]
  );

  return (
    <Section>
      <Card
        title={i18n.t('general.labels.documents')}
        actions={buildArray<Action>()
          .whenNot(props.isReadOnly, {
            variant: 'link',
            type: 'button',
            leftIcon: 'action/backup',
            onClick: openUploadDialog,
            title: i18n.t('entity.document.actions.uploadDocument'),
            'data-testid': testIds.workshop.serviceCaseDetail('add-document'),
          })
          .add(...bulkSignatureAction)}
      >
        <DataStatus
          isLoading={isSelectedOwnershipLoading}
          isError={isSelectedOwnershipError}
          minHeight={33}
        >
          <Box minHeight={40}>
            <DmsDataGrid
              gridCode="document-context-list-service-case"
              actionCallback={actionCallback}
              predefinedColumns={DOCUMENT_ACTION_COLUMNS}
              queryModifier={queryModifier}
              autoHeight
              data-testid={testIds.workshop.serviceCaseDetail('documents')}
            />
          </Box>
        </DataStatus>
      </Card>
      <UploadDocumentsDialog
        isOpen={isUploadDialogOpen}
        handleClose={closeUploadDialog}
        onDocumentCreated={handleDocumentCreated}
        contextTarget="service-case"
        contextId={serviceCaseId}
        bulkContext={[{target: 'service-case', targetId: serviceCaseId}]}
        data-testid={testIds.workshop.serviceCaseDetail('upload-document')}
      />
    </Section>
  );
}
